import { Button } from '@/components/ui';
import TooltipWrapper from '@/components/ui/tooltip';
import useCancelOrders from '@/features/trade/order/api/cancel-orders';
import {
  getMigrationBlockTooltipKey,
  useMigrationTradingBlock,
} from '@/hooks/use-migration';
import {
  getIsWeekendTradingBlocked,
  useWeekendPauseTick,
} from '@/hooks/use-weekend-trading';
import { useAccountStore } from '@/store/use-account-store';
import { useOrdersStore } from '@/store/use-orders-store';
import { OrderType } from '@/types';
import { useTranslation } from 'react-i18next';

// `id:symbol` pairs joined into one string — a primitive keeps the zustand
// snapshot stable across renders (an array would be a new reference each call).
const ORDER_SEPARATOR = ',';
const FIELD_SEPARATOR = ':';

export default function CancelAllOrders() {
  const { t } = useTranslation();
  const address = useAccountStore((state) => state.address);

  const openOrdersKey = useOrdersStore((state) => {
    const { orders, newOrderIds } = state.getOrders(address);
    return Array.from(newOrderIds, (id) => orders[id])
      .filter((order) => order && order.orderType !== OrderType.MARKET)
      .map((order) => `${order.id}${FIELD_SEPARATOR}${order.symbol}`)
      .join(ORDER_SEPARATOR);
  });

  // Orders on a paused market cannot be cancelled either, so "cancel all" only
  // ever submits the ids it is still allowed to touch.
  useWeekendPauseTick();
  const { isBlocked: isMigrationBlocked, reason: migrationReason } =
    useMigrationTradingBlock();

  const { mutate: cancelOrders, isPending: isCancelling } = useCancelOrders({
    mutationKey: ['/orders/cancel/all'],
  });

  const openOrders = openOrdersKey
    ? openOrdersKey.split(ORDER_SEPARATOR).map((entry) => {
        const [id, symbol] = entry.split(FIELD_SEPARATOR);
        return { id, symbol };
      })
    : [];

  if (openOrders.length <= 1) {
    return;
  }

  const cancellableIds = openOrders
    .filter((order) => !getIsWeekendTradingBlocked(order.symbol))
    .map((order) => order.id);
  const isWeekendBlocked = cancellableIds.length === 0;
  const isBlocked = isWeekendBlocked || isMigrationBlocked;

  const button = (
    <Button
      variant="outline"
      disabled={isCancelling || isBlocked}
      onClick={() => {
        if (isBlocked) return;
        cancelOrders({ id: cancellableIds, showToast: true });
      }}
    >
      Cancel All
    </Button>
  );

  if (isMigrationBlocked) {
    return (
      <TooltipWrapper
        content={t(getMigrationBlockTooltipKey(migrationReason))}
        direction="bottom"
        portal
      >
        {button}
      </TooltipWrapper>
    );
  }

  if (isWeekendBlocked) {
    return (
      <TooltipWrapper
        content={t('weekendPause.cancelBlocked')}
        direction="bottom"
        portal
      >
        {button}
      </TooltipWrapper>
    );
  }

  return button;
}
